import { IN_STOCK } from '@jouzen/ecom-components/v3/consts/stockStatus';
import type { RingSizeData } from '@jouzen/ecom-components/v3/types/ringSizeData';
import type { StockStatus } from '@jouzen/ecom-components/v3/types/stockStatus';
import { useMemo } from 'react';

import type { CurrentRouteValues } from '@/app/[locale]/store/_components/HardwarePDP/_hooks/useCurrentRoute';
import type { ListProduct } from '@/queries/Products';

interface UseRingPDPProductsDataProps {
  chargerSetHandle: string;
  currentRoute: CurrentRouteValues;
  productsData: ListProduct[] | undefined;
  ringHandlePrefix: string;
}

export interface UseRingPDPProductsDataState {
  chargerSet: ListProduct | undefined;
  oneMonthMembershipSku: string;
  oneYearMembership: ListProduct | undefined;
  oneYearMembershipSku: string;
  product: ListProduct | undefined;
  ringSizesData: RingSizeData[] | undefined;
  steloGlucoseBiosensor: ListProduct | undefined;
  subscription: ListProduct | undefined;
  threeYearWarranty: ListProduct | undefined;
  threeYearWarrantySku: string;
  twoYearWarranty: ListProduct | undefined;
  twoYearWarrantySku: string;
}

const useRingPDPProductsData = ({
  chargerSetHandle,
  currentRoute,
  productsData,
  ringHandlePrefix,
}: UseRingPDPProductsDataProps): UseRingPDPProductsDataState => {
  const product: ListProduct | undefined = productsData?.find((product) => {
    const handle = product.handle;
    return handle === `${ringHandlePrefix}-${currentRoute.finish.value}`;
  });

  const subscription: ListProduct | undefined = productsData?.find(
    ({ handle }: { handle: ListProduct['handle'] }) => {
      return handle === 'subscription';
    },
  );

  const oneYearMembership: ListProduct | undefined = productsData?.find(
    ({ handle }: { handle: ListProduct['handle'] }) =>
      handle === 'one-year-membership',
  );

  const twoYearWarranty: ListProduct | undefined = productsData?.find(
    ({ handle }: { handle: ListProduct['handle'] }) =>
      handle === 'extended-warranty-2year',
  );

  const threeYearWarranty: ListProduct | undefined = productsData?.find(
    ({ handle }: { handle: ListProduct['handle'] }) =>
      handle === 'extended-warranty-3year',
  );

  const steloGlucoseBiosensor: ListProduct | undefined = productsData?.find(
    ({ handle }: { handle: ListProduct['handle'] }) =>
      handle === 'stelo-dexcom-cgm',
  );

  const chargerSet: ListProduct | undefined = productsData?.find(
    ({ handle }: { handle: ListProduct['handle'] }) =>
      handle === chargerSetHandle,
  );

  const ringSizesData = useMemo(() => {
    return product?.variants.map((variant) => ({
      size: variant.selectedOptions[0]?.value ?? '',
      sku: variant.sku,
      stockStatus: (variant.inventory?.retail ?? IN_STOCK) as StockStatus,
    }));
  }, [product?.variants]);

  const oneMonthMembershipSku = subscription?.variants[0]?.sku ?? '';
  const oneYearMembershipSku = oneYearMembership?.variants[0]?.sku ?? '';
  const twoYearWarrantySku = twoYearWarranty?.variants[0]?.sku ?? '';
  const threeYearWarrantySku = threeYearWarranty?.variants[0]?.sku ?? '';

  return {
    chargerSet,
    oneMonthMembershipSku,
    oneYearMembership,
    oneYearMembershipSku,
    product,
    ringSizesData,
    steloGlucoseBiosensor,
    subscription,
    threeYearWarranty,
    threeYearWarrantySku,
    twoYearWarranty,
    twoYearWarrantySku,
  };
};

export default useRingPDPProductsData;
